import { integrationApi } from "@repo/api";
import { InputField, Typography } from "@saviynt/design-system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { theme } from "../../../constants/theme";
import { useIntegrationById } from "../../../hooks/useIntegrationById";
import { useIntegrationTypeFeatures } from "../../../hooks/useIntegrationTypeFeatures";
import { useTestConnection } from "../../../hooks/useTestConnection";
import {
  type Frequency,
  DEFAULT_FREQUENCY,
  parseCronToSchedule,
} from "../../../utils/schedule";
import { FormSection } from "../../FormSection";
import { IntegrationTypeSection } from "../../shared/IntegrationTypeSection";
import { TestConnectionBlock } from "../../TestConnectionBlock";
import { AdditionalConfigurationSection } from "../../aws/AWSIntegrationForm/AdditionalConfigurationSection";
import { getDefaultTime } from "../../aws/AWSIntegrationForm/utils";

export interface AgentforceDiscoveryFormData {
  name: string;
  description: string;
  clientId: string;
  clientSecret: string;
  domain: string;
  organizationId: string;
  isValid: boolean;
  cftDownloaded: boolean;
}

interface AgentforceDiscoveryFormProps {
  integrationType: string;
  integrationId?: number;
  onFormDataChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

export function AgentforceDiscoveryForm({
  integrationType,
  integrationId,
  onFormDataChange,
}: AgentforceDiscoveryFormProps) {
  const intl = useIntl();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [domain, setDomain] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [clientSecretVisible, setClientSecretVisible] = useState(false);
  const [importTime, setImportTime] = useState<Date | null>(() =>
    getDefaultTime(),
  );
  const [frequency, setFrequency] = useState<Frequency>(DEFAULT_FREQUENCY);
  const hasPrefilledIntegrationRef = useRef(false);
  const hasPrefilledCredentialsRef = useRef(false);
  const onFormDataChangeRef = useRef(onFormDataChange);
  onFormDataChangeRef.current = onFormDataChange;

  const { data: integrationData } = useIntegrationById(integrationId);
  const { data: credentials } = useQuery({
    queryKey: ["integrationCredentials", integrationId],
    queryFn: async () => {
      if (!integrationId) throw new Error("Integration ID required");
      return integrationApi.getIntegrationCredentials(integrationId);
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: featuresResponse } =
    useIntegrationTypeFeatures(integrationType);
  const featureNames = useMemo(
    () =>
      featuresResponse?.features
        ? featuresResponse.features.map((f) => f.featureName)
        : [],
    [featuresResponse?.features],
  );

  useEffect(() => {
    if (!integrationId || !integrationData) return;

    if (!hasPrefilledIntegrationRef.current) {
      setName(integrationData.displayName ?? "");
      setDescription((integrationData.metadata?.description as string) ?? "");
      setDomain(
        (integrationData.metadata?.domain as string) ??
          ((credentials?.data as Record<string, unknown> | undefined)
            ?.domain as string) ??
          "",
      );
      setOrganizationId((integrationData.metadata?.accountId as string) ?? "");

      if (
        integrationData.features?.length &&
        integrationData.features[0].schedules?.length
      ) {
        const cronExpression =
          integrationData.features[0].schedules[0].cronExpression;
        if (cronExpression) {
          const parsed = parseCronToSchedule(cronExpression);
          setFrequency(parsed.frequency);
          if (parsed.time) {
            setImportTime(parsed.time);
          }
        }
      }
      hasPrefilledIntegrationRef.current = true;
    }

    const credsData = credentials?.data as
      | { clientId?: string; clientSecret?: string; domain?: string }
      | undefined;
    const prefilledClientId = credsData?.clientId;
    if (
      prefilledClientId != null &&
      prefilledClientId !== "" &&
      !hasPrefilledCredentialsRef.current
    ) {
      setClientId(prefilledClientId);
      if (credsData?.clientSecret != null && credsData.clientSecret !== "") {
        setClientSecret(credsData.clientSecret);
      }
      if (credsData?.domain) setDomain(credsData.domain);
      hasPrefilledCredentialsRef.current = true;
    }
  }, [
    integrationId,
    integrationData,
    credentials?.data,
    credentials?.metadata,
  ]);

  const testConnection = useTestConnection(() => {
    const trimmedClientId = clientId.trim();
    const trimmedClientSecret = clientSecret.trim();
    const trimmedDomain = domain.trim();
    if (!trimmedClientId || !trimmedClientSecret || !trimmedDomain) {
      return null;
    }
    return {
      integrationType: "agentforce",
      credentials: {
        type: "OAUTH2",
        data: {
          clientId: trimmedClientId,
          clientSecret: trimmedClientSecret,
          domain: trimmedDomain,
        },
        metadata: {},
      },
      integrationParameters: {},
    };
  });

  const isNameValid = name.trim().length > 0;
  const isClientIdValid = clientId.trim().length > 0;
  const isClientSecretValid =
    clientSecret.trim().length > 0 || (!!integrationId && !!clientId);
  const isDomainValid = domain.trim().length > 0;
  const isOrganizationIdValid = organizationId.trim().length > 0;
  const isValid =
    isNameValid &&
    isClientIdValid &&
    isClientSecretValid &&
    isDomainValid &&
    isOrganizationIdValid &&
    (frequency !== "24hours" || importTime !== null);
  const cftDownloaded = isClientIdValid && isClientSecretValid && isDomainValid;

  useEffect(() => {
    onFormDataChangeRef.current({
      name: name.trim(),
      description: description.trim(),
      clientId: clientId.trim(),
      clientSecret: clientSecret,
      domain: domain.trim(),
      organizationId: organizationId.trim(),
      frequency,
      importTime,
      isValid,
      cftDownloaded,
      featureNames,
      integrationType,
    });
  }, [
    name,
    description,
    clientId,
    clientSecret,
    domain,
    organizationId,
    frequency,
    importTime,
    isValid,
    cftDownloaded,
    featureNames,
    integrationType,
  ]);

  return (
    <div
      className="Integrations-addIntegrationForm flex flex-col h-full w-full p-4"
      data-testid="agentforce-discovery-form"
    >
      <div className="flex-1 overflow-auto p-4">
        <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
          <IntegrationTypeSection integrationType={integrationType} />
          <FormSection
            icon="Connect"
            title={intl.formatMessage({
              id: "6c0sHj",
              defaultMessage:
                "What are the connection details of your integration?",
              description: "Connection details section title",
            })}
            subtitle={intl.formatMessage({
              id: "KlVUsw",
              defaultMessage:
                "Some fields have been pre-filled for you, you can update them whenever you like.",
              description: "Connection details section subtitle",
            })}
          >
            <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
              <InputField
                name="name"
                label={
                  <Typography kind="label" htmlFor="agentforce-name">
                    <FormattedMessage
                      id="JeJjeJ"
                      defaultMessage="Integration Name"
                      description="Integration name field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={name}
                setValue={setName}
                isRequired
                isDisabled={!!integrationId}
              />

              <div>
                <Typography
                  kind="label"
                  className="block mb-1"
                  htmlFor="agentforce-description"
                >
                  <FormattedMessage
                    id="TjUCGI"
                    defaultMessage="Integration Description"
                    description="Integration description field label"
                  />
                </Typography>
                <InputField
                  name="description"
                  placeholder=""
                  value={description}
                  setValue={setDescription}
                  kind="multiline"
                  rows={4}
                />
              </div>

              <InputField
                name="clientId"
                label={
                  <Typography kind="label" htmlFor="agentforce-client-id">
                    <FormattedMessage
                      id="YB3Qau"
                      defaultMessage="Client ID"
                      description="Client ID field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={clientId}
                setValue={setClientId}
                isRequired
              />

              <InputField
                name="clientSecret"
                label={
                  <Typography kind="label" htmlFor="agentforce-client-secret">
                    <FormattedMessage
                      id="Og17uQ"
                      defaultMessage="Client Secret"
                      description="Client Secret field label"
                    />
                  </Typography>
                }
                placeholder=""
                type={clientSecretVisible ? "text" : "password"}
                value={clientSecret}
                setValue={setClientSecret}
                isRequired={!integrationId}
                suffixButtonType="password"
                suffixIsToggle={clientSecretVisible}
                suffixOnClick={() =>
                  setClientSecretVisible(!clientSecretVisible)
                }
              />

              <InputField
                name="domain"
                label={
                  <Typography kind="label" htmlFor="agentforce-domain">
                    <FormattedMessage
                      id="PNbl++"
                      defaultMessage="Domain"
                      description="Domain field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={domain}
                setValue={setDomain}
                isRequired
              />

              <InputField
                name="organizationId"
                label={
                  <Typography kind="label" htmlFor="agentforce-organization-id">
                    <FormattedMessage
                      id="pHnLf2"
                      defaultMessage="Organization ID"
                      description="Organization ID field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={organizationId}
                setValue={setOrganizationId}
                isRequired
              />

              <TestConnectionBlock
                isTestingConnection={testConnection.isTestingConnection}
                connectionStatus={testConnection.connectionStatus}
                onTestConnection={testConnection.handleTestConnection}
                isDisabled={
                  !isClientIdValid || !clientSecret.trim() || !isDomainValid
                }
              />
            </div>
          </FormSection>

          <AdditionalConfigurationSection
            initialTime={importTime}
            initialFrequency={frequency}
            onScheduleChange={(freq, time) => {
              setFrequency(freq as Frequency);
              setImportTime(time);
            }}
            isRequired={true}
            isCritical={frequency === "24hours" && importTime === null}
          />
        </div>
      </div>
    </div>
  );
}
